import React, { useState, useEffect } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Button } from '@mui/material';
import { InvestmentType, CurrencyCode } from '../../types/stocks';
import { investmentTypeToLabel } from '../../utils/investmentType';

export interface StockFormValues {
  symbol: string;
  investmentType: InvestmentType;
  currentPrice?: number | '';
  currentCambio?: number | '';
  currency: CurrencyCode;
}

interface StockFormProps {
  open: boolean;
  initialValues?: StockFormValues;
  onClose: () => void;
  onSubmit: (values: StockFormValues) => void;
}

export default function StockForm({ open, initialValues, onClose, onSubmit }: StockFormProps) {
  const [values, setValues] = useState<StockFormValues>(
    initialValues || { symbol: '', investmentType: InvestmentType.Stock, currentPrice: '', currentCambio: '', currency: CurrencyCode.EUR }
  );

  useEffect(() => {
    setValues(initialValues || { symbol: '', investmentType: InvestmentType.Stock, currentPrice: '', currentCambio: '', currency: CurrencyCode.EUR });
  }, [initialValues, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: (name === 'currentPrice' || name === 'currentCambio')
        ? (value === '' ? '' : Number(value))
        : (name === 'investmentType' ? Number(value) : value)
    });
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...values });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initialValues ? 'Editar ação' : 'Criar ação'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            label="Símbolo"
            name="symbol"
            value={values.symbol}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            select
            label="Tipo de investimento"
            name="investmentType"
            value={values.investmentType}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {Object.keys(InvestmentType)
              .filter(k => isNaN(Number(k)))
              .map(name => {
                const val = (InvestmentType as any)[name] as number;
                return (
                  <MenuItem key={val} value={val}>{investmentTypeToLabel(val)}</MenuItem>
                );
              })}
          </TextField>
          <TextField
            label="Preço atual"
            name="currentPrice"
            type="number"
            value={values.currentPrice}
            onChange={handleChange}
            fullWidth
            margin="normal"
            inputProps={{ step: values.investmentType === InvestmentType.Cryptocurrency ? '0.000000001' : '0.01', min: '0' }}
          />
          <TextField
            label="Câmbio (cotação → EUR)"
            name="currentCambio"
            type="number"
            value={values.currentCambio}
            onChange={handleChange}
            fullWidth
            margin="normal"
            inputProps={{ step: values.investmentType === InvestmentType.Cryptocurrency ? '0.000000001' : '0.000001', min: '0' }}
          />
          <TextField
            select
            label="Moeda"
            name="currency"
            value={values.currency}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {Object.values(CurrencyCode).map(c => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained">{initialValues ? 'Atualizar' : 'Criar'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
